import { PublicKey, TransactionInstruction } from '@solana/web3.js';
import { TOKEN_2022_PROGRAM_ID, ACCOUNT_SIZE } from '@solana/spl-token';

// Token-2022 wire-format constants for the Confidential Transfer extension.
// We hand-build the InitializeMint instruction because @solana/spl-token does
// not export a helper for it.
export const CONFIDENTIAL_TRANSFER_EXTENSION_TYPE = 4;
const CONFIDENTIAL_TRANSFER_OUTER_DISCRIMINATOR = 27;
const CONFIDENTIAL_TRANSFER_INIT_INNER = 0;

const CT_TLV_DATA_LEN = 65;
const TLV_HEADER_LEN = 4;
const ACCOUNT_TYPE_SIZE = 1;
export const CONFIDENTIAL_TRANSFER_MINT_TLV_TOTAL_LEN = TLV_HEADER_LEN + CT_TLV_DATA_LEN; // 4 + 65 = 69

const ZERO_PUBKEY = new PublicKey(new Uint8Array(32));
const ZERO_ELGAMAL = new Uint8Array(32);

/**
 * InitializeMint(authority, auto_approve_new_accounts, auditor_elgamal_pubkey).
 *
 * We pass null/zero for any field the user wants None. The fourth on-chain field
 * (withdraw_withheld_authority_elgamal_pubkey) is set by a separate instruction
 * later if needed and defaults to None here.
 */
export function createInitializeConfidentialTransferMintInstruction(
  mint: PublicKey,
  authority: PublicKey | null,
  autoApproveNewAccounts: boolean,
  auditorElGamalPubkey: Uint8Array | null,
  programId: PublicKey = TOKEN_2022_PROGRAM_ID,
): TransactionInstruction {
  if (auditorElGamalPubkey && auditorElGamalPubkey.length !== 32) {
    throw new Error('Auditor ElGamal pubkey must be 32 bytes (Ristretto255 encoding).');
  }
  const data = Buffer.alloc(2 + 32 + 1 + 32);
  data.writeUInt8(CONFIDENTIAL_TRANSFER_OUTER_DISCRIMINATOR, 0);
  data.writeUInt8(CONFIDENTIAL_TRANSFER_INIT_INNER, 1);
  const authBytes = (authority ?? ZERO_PUBKEY).toBuffer();
  authBytes.copy(data, 2);
  data.writeUInt8(autoApproveNewAccounts ? 1 : 0, 34);
  Buffer.from(auditorElGamalPubkey ?? ZERO_ELGAMAL).copy(data, 35);
  return new TransactionInstruction({
    programId,
    keys: [{ pubkey: mint, isSigner: false, isWritable: true }],
    data,
  });
}

export interface ConfidentialTransferMintConfig {
  authority: PublicKey | null;
  autoApproveNewAccounts: boolean;
  auditorElGamalPubkey: Uint8Array | null;
}

const isAllZero = (bytes: Uint8Array): boolean => {
  for (let i = 0; i < bytes.length; i++) {
    if (bytes[i] !== 0) {return false;}
  }
  return true;
};

/**
 * Walk the Token-2022 mint TLV table looking for ConfidentialTransferMint.
 * Returns null when the extension is absent or the buffer is malformed.
 */
export function parseConfidentialTransferMintFromMint(
  buffer: Buffer,
): ConfidentialTransferMintConfig | null {
  const tlvStart = ACCOUNT_SIZE + ACCOUNT_TYPE_SIZE;
  if (buffer.length <= tlvStart) {return null;}

  let cursor = tlvStart;
  while (cursor + TLV_HEADER_LEN <= buffer.length) {
    const type = buffer.readUInt16LE(cursor);
    const length = buffer.readUInt16LE(cursor + 2);
    const dataStart = cursor + TLV_HEADER_LEN;
    const dataEnd = dataStart + length;
    if (dataEnd > buffer.length) {return null;}

    if (type === CONFIDENTIAL_TRANSFER_EXTENSION_TYPE) {
      if (length < CT_TLV_DATA_LEN) {return null;}
      const authorityBytes = buffer.slice(dataStart, dataStart + 32);
      const autoApproveByte = buffer.readUInt8(dataStart + 32);
      const auditorBytes = new Uint8Array(buffer.slice(dataStart + 33, dataStart + 65));
      return {
        authority: isAllZero(authorityBytes) ? null : new PublicKey(authorityBytes),
        autoApproveNewAccounts: autoApproveByte !== 0,
        auditorElGamalPubkey: isAllZero(auditorBytes) ? null : auditorBytes,
      };
    }

    if (type === 0 && length === 0) {
      cursor += TLV_HEADER_LEN;
      continue;
    }
    cursor = dataEnd;
  }
  return null;
}

/**
 * Match the outer discriminator (27) on a Token-2022 instruction so we can pluck
 * confidential-transfer instructions out of a fetched transaction.
 */
export function isConfidentialTransferInstructionData(data: Uint8Array): boolean {
  return data.length >= 1 && data[0] === CONFIDENTIAL_TRANSFER_OUTER_DISCRIMINATOR;
}

/**
 * Inner discriminator for Transfer/TransferWithFee instructions. Useful for the
 * auditor decrypt tool to filter the instructions we know how to parse.
 */
export const CONFIDENTIAL_TRANSFER_TRANSFER_INNER = 7;
export const CONFIDENTIAL_TRANSFER_TRANSFER_WITH_SPLIT_PROOFS_INNER = 13;
